import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useState } from 'react';
import { cn } from '../cn.js';
import { Skeleton } from '../components/skeleton.js';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/table.js';
import { IconChevronDown } from '../icons.js';

/**
 * A column, re-exported from TanStack Table.
 *
 * `DataTableProps` names this type, so a consumer cannot describe a table without it — and an
 * application may not depend on the computing library directly: that is the design system's
 * dependency, chosen because it ships no DOM and no styles (ADR-0018), and an application that
 * imported it could start rendering with it.
 */
export type { ColumnDef } from '@tanstack/react-table';

export interface DataTableProps<Row> {
  /** The table's accessible name. Visually hidden; a page heading usually says it too. */
  readonly caption: string;
  readonly columns: readonly ColumnDef<Row, unknown>[];
  readonly data: readonly Row[];
  readonly loading?: boolean;
  /** Shown instead of rows when there are none. An `EmptyState` belongs here. */
  readonly empty?: ReactNode;
  /** Opening a row. Given this, rows become activatable by click **and by keyboard**. */
  readonly onRowActivate?: (row: Row) => void;
  readonly rowIsSelected?: (row: Row) => boolean;
  readonly className?: string;
}

/**
 * A table of records, with the behaviour an operational screen needs.
 *
 * TanStack Table computes — sorting, row models, column definitions — and the `Table` primitive
 * renders. That split is why the dependency is safe to take: it ships no DOM and no styles, so it
 * cannot disagree with the design system. It is what ADR-0018 specified, and before this the
 * product had seven hand-written `<table>` blocks, each slightly different.
 *
 * **A sortable header is a button inside the `<th>`, not a clickable `<th>`.** A cell is not
 * focusable and a screen reader does not announce it as actionable; the `aria-sort` on the header
 * says which way the column is ordered, and the button is what a keyboard reaches.
 *
 * **An activatable row is handled on the row, and on the keyboard too.** A `<tr>` with only an
 * `onClick` is a mouse-only feature, so it takes a `button` role, a tab stop and Enter/Space.
 * Where a row has its own link or menu, that control stops the event — a row action must not also
 * open the row.
 */
export function DataTable<Row>({
  caption,
  columns,
  data,
  loading = false,
  empty,
  onRowActivate,
  rowIsSelected,
  className,
}: DataTableProps<Row>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data: data as Row[],
    columns: columns as ColumnDef<Row, unknown>[],
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) {
    return (
      <Table className={className}>
        <TableCaption>{caption}</TableCaption>
        <TableHeader>
          <TableRow>
            {table.getFlatHeaders().map((header) => (
              <TableHead key={header.id}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Rows shaped like the content, not a spinner: the table's width is already known, so
              the page does not jump when the data lands. */}
          {[0, 1, 2].map((row) => (
            <TableRow key={row}>
              {table.getFlatHeaders().map((header) => (
                <TableCell key={header.id}>
                  <Skeleton className="h-(--space-md) w-full" label={`${caption}, loading`} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (data.length === 0 && empty !== undefined) {
    return <>{empty}</>;
  }

  return (
    <Table className={className}>
      <TableCaption>{caption}</TableCaption>
      <TableHeader>
        {table.getHeaderGroups().map((group) => (
          <TableRow key={group.id}>
            {group.headers.map((header) => {
              const sortable = header.column.getCanSort();
              const direction = header.column.getIsSorted();
              return (
                <TableHead
                  key={header.id}
                  sort={
                    direction === 'asc'
                      ? 'ascending'
                      : direction === 'desc'
                        ? 'descending'
                        : sortable
                          ? 'none'
                          : undefined
                  }
                >
                  {header.isPlaceholder ? null : sortable ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        'inline-flex items-center gap-(--space-xs)',
                        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring)',
                        'hover:text-(--color-text-primary)',
                      )}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <IconChevronDown
                        aria-hidden="true"
                        className={cn(
                          'size-(--size-icon-sm) transition-transform duration-(--motion-duration-fast) motion-reduce:transition-none',
                          direction === 'asc' && 'rotate-180',
                          direction === false && 'opacity-0 group-hover:opacity-100',
                        )}
                      />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow
            key={row.id}
            selected={rowIsSelected?.(row.original) ?? false}
            interactive={onRowActivate !== undefined}
            {...(onRowActivate === undefined
              ? {}
              : {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => onRowActivate(row.original),
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowActivate(row.original);
                    }
                  },
                })}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
